/**
 * Memory monitoring and alerting for the tracing system
 * Helps prevent memory leaks and provides early warnings
 */

export interface MemoryStats {
  heapUsed: number;
  heapTotal: number;
  external: number;
  rss: number;
  timestamp: Date;
}

export interface MemoryAlert {
  level: 'warning' | 'critical';
  message: string;
  stats: MemoryStats;
  threshold: number;
  timestamp: Date;
}

export interface MemoryMonitorConfig {
  // Thresholds (in MB)
  warningThresholdMB: number;
  criticalThresholdMB: number;
  
  // Monitoring frequency
  checkIntervalSeconds: number;
  
  // History tracking
  maxHistoryEntries: number;
  
  // Alert callbacks
  onWarning?: (alert: MemoryAlert) => void;
  onCritical?: (alert: MemoryAlert) => void;
}

export const DEFAULT_MONITOR_CONFIG: MemoryMonitorConfig = {
  warningThresholdMB: 100,   // 100MB heap used
  criticalThresholdMB: 200,  // 200MB heap used
  checkIntervalSeconds: 30,   // Check every 30 seconds
  maxHistoryEntries: 100,    // Keep last 100 readings
};

export class MemoryMonitor {
  private config: MemoryMonitorConfig;
  private history: MemoryStats[] = [];
  private alerts: MemoryAlert[] = [];
  private monitorTimer: NodeJS.Timeout | null = null;
  private isMonitoring: boolean = false;
  
  constructor(config: Partial<MemoryMonitorConfig> = {}) {
    this.config = { ...DEFAULT_MONITOR_CONFIG, ...config };
  }
  
  /**
   * Start memory monitoring
   */
  start(): void {
    if (this.isMonitoring) {
      return;
    }
    
    this.isMonitoring = true;
    const intervalMs = this.config.checkIntervalSeconds * 1000;
    
    this.monitorTimer = setInterval(() => {
      this.checkMemory();
    }, intervalMs);
    
    // Take initial reading
    this.checkMemory();
    
    console.log(`📊 Memory monitoring started (${this.config.checkIntervalSeconds}s intervals)`);
  }
  
  /**
   * Stop memory monitoring
   */
  stop(): void {
    if (this.monitorTimer) {
      clearInterval(this.monitorTimer);
      this.monitorTimer = null;
    }
    
    this.isMonitoring = false;
    console.log('📊 Memory monitoring stopped');
  }
  
  /**
   * Take a memory reading and check thresholds
   */
  private checkMemory(): void {
    const stats = this.getMemoryStats();
    
    // Add to history
    this.history.push(stats);
    
    // Trim history to max size
    if (this.history.length > this.config.maxHistoryEntries) {
      this.history = this.history.slice(-this.config.maxHistoryEntries);
    }
    
    // Check thresholds
    this.checkThresholds(stats);
  }
  
  /**
   * Get current memory statistics
   */
  getMemoryStats(): MemoryStats {
    const usage = process.memoryUsage();
    
    return {
      heapUsed: usage.heapUsed,
      heapTotal: usage.heapTotal,
      external: usage.external,
      rss: usage.rss,
      timestamp: new Date()
    };
  }
  
  /**
   * Check if memory usage exceeds thresholds
   */
  private checkThresholds(stats: MemoryStats): void {
    const heapUsedMB = stats.heapUsed / 1024 / 1024;
    
    // Check critical threshold
    if (heapUsedMB > this.config.criticalThresholdMB) {
      const alert: MemoryAlert = {
        level: 'critical',
        message: `Critical memory usage: ${Math.round(heapUsedMB)}MB heap used (threshold: ${this.config.criticalThresholdMB}MB)`,
        stats,
        threshold: this.config.criticalThresholdMB,
        timestamp: new Date()
      };
      
      this.alerts.push(alert);
      console.error('🚨 CRITICAL MEMORY ALERT:', alert.message);
      
      if (this.config.onCritical) {
        this.config.onCritical(alert);
      }
      
    } else if (heapUsedMB > this.config.warningThresholdMB) {
      // Check warning threshold
      const alert: MemoryAlert = {
        level: 'warning',
        message: `High memory usage: ${Math.round(heapUsedMB)}MB heap used (threshold: ${this.config.warningThresholdMB}MB)`,
        stats,
        threshold: this.config.warningThresholdMB,
        timestamp: new Date()
      };
      
      this.alerts.push(alert);
      console.warn('⚠️  MEMORY WARNING:', alert.message);
      
      if (this.config.onWarning) {
        this.config.onWarning(alert);
      }
    }
    
    // Trim alerts to prevent them from growing indefinitely
    if (this.alerts.length > 50) {
      this.alerts = this.alerts.slice(-50);
    }
  }
  
  /**
   * Get memory monitoring status and statistics
   */
  getStatus(): {
    isMonitoring: boolean;
    current: MemoryStats;
    history: MemoryStats[];
    alerts: MemoryAlert[];
    thresholds: {
      warningMB: number;
      criticalMB: number;
    };
    trend: {
      direction: 'increasing' | 'decreasing' | 'stable';
      changePercent: number;
    };
  } {
    const current = this.getMemoryStats();
    const trend = this.calculateTrend();
    
    return {
      isMonitoring: this.isMonitoring,
      current,
      history: this.history.slice(), // Copy to prevent external modification
      alerts: this.alerts.slice(),
      thresholds: {
        warningMB: this.config.warningThresholdMB,
        criticalMB: this.config.criticalThresholdMB
      },
      trend
    };
  }
  
  /**
   * Calculate memory usage trend
   */
  private calculateTrend(): {
    direction: 'increasing' | 'decreasing' | 'stable';
    changePercent: number;
  } {
    if (this.history.length < 2) {
      return { direction: 'stable', changePercent: 0 };
    }
    
    const recent = this.history.slice(-5); // Last 5 readings
    const first = recent[0].heapUsed;
    const last = recent[recent.length - 1].heapUsed;
    
    const changePercent = ((last - first) / first) * 100;
    
    let direction: 'increasing' | 'decreasing' | 'stable';
    if (Math.abs(changePercent) < 5) {
      direction = 'stable';
    } else if (changePercent > 0) {
      direction = 'increasing';
    } else {
      direction = 'decreasing';
    }
    
    return { direction, changePercent };
  }
  
  /**
   * Get recent alerts
   */
  getRecentAlerts(count: number = 10): MemoryAlert[] {
    return this.alerts.slice(-count);
  }
  
  /**
   * Clear alert history
   */
  clearAlerts(): void {
    this.alerts = [];
  }
  
  /**
   * Update monitoring configuration
   */
  updateConfig(newConfig: Partial<MemoryMonitorConfig>): void {
    const wasMonitoring = this.isMonitoring;
    
    if (wasMonitoring) {
      this.stop();
    }
    
    this.config = { ...this.config, ...newConfig };
    
    if (wasMonitoring) {
      this.start();
    }
    
    console.log('📊 Memory monitor configuration updated:', {
      warningThreshold: this.config.warningThresholdMB + 'MB',
      criticalThreshold: this.config.criticalThresholdMB + 'MB',
      checkInterval: this.config.checkIntervalSeconds + 's'
    });
  }
  
  /**
   * Force garbage collection if available (Node.js --expose-gc flag)
   */
  forceGarbageCollection(): boolean {
    if (global.gc) {
      console.log('🗑️  Forcing garbage collection...');
      global.gc();
      return true;
    } else {
      console.warn('⚠️  Garbage collection not available (run with --expose-gc to enable)');
      return false;
    }
  }
}